/**
 * Аудит входящих API-вызовов от внешних партнёров (Avito и др.).
 *
 * Бэкенд (репо `stryzh`): таблица `public.external_api_log` (миграция
 * 20260602100000). Пишется edge-функцией `avito-stats` на каждый вызов
 * (200/401/400/404/405/500). RLS: SELECT только для admin/dispatcher.
 */
import { createClient } from '@/api/supabase'

export interface ExternalApiLogRow {
  id: number
  called_at: string
  partner: string
  endpoint: string
  lead_id: string | null
  status_code: number
  reason: string | null
  remote_ip: string | null
  user_agent: string | null
  duration_ms: number | null
}

/**
 * Человеческое «направление» вызова по endpoint:
 *  - avito-lead  — входящая заявка (Avito присылает лид нам);
 *  - avito-stats — исходящий опрос статуса (Avito спрашивает — мы отвечаем).
 * kind используется для цвета бейджа; 'other' — на случай новых эндпойнтов.
 */
export function apiLogDirection(
  endpoint: string,
): { label: string; kind: 'in' | 'out' | 'other' } {
  switch (endpoint) {
    case 'avito-lead':
      return { label: 'Входящий лид', kind: 'in' }
    case 'avito-stats':
      return { label: 'Запрос статуса', kind: 'out' }
    default:
      return { label: endpoint, kind: 'other' }
  }
}

export interface ListExternalApiLogParams {
  partner?: string
  endpoint?: string
  /** Фильтр по lead_id (показывает только запросы по конкретному лиду). */
  leadId?: string
  limit?: number
  /** OFFSET для пагинации (если потребуется в будущем). */
  offset?: number
}

const DEFAULT_LIMIT = 100

export async function listExternalApiLog(
  params: ListExternalApiLogParams = {},
): Promise<ExternalApiLogRow[]> {
  const supabase = createClient()
  let q = supabase
    .from('external_api_log')
    .select('id, called_at, partner, endpoint, lead_id, status_code, reason, remote_ip, user_agent, duration_ms')
    .order('id', { ascending: false })
    .limit(params.limit ?? DEFAULT_LIMIT)
  if (params.partner) q = q.eq('partner', params.partner)
  if (params.endpoint) q = q.eq('endpoint', params.endpoint)
  if (params.leadId) q = q.eq('lead_id', params.leadId)
  if (params.offset && params.offset > 0) {
    q = q.range(params.offset, params.offset + (params.limit ?? DEFAULT_LIMIT) - 1)
  }
  const { data, error } = await q
  if (error) throw new Error(error.message)
  return (data ?? []) as ExternalApiLogRow[]
}
